import { Inject, Injectable, PLATFORM_ID } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { merge, using } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators'
import {
  CmsService,
  EventService,
  LoadingScopesService,
  Page,
  PageType,
  ProductLoadingService,
  ProductSelectors,
  StateWithProduct
} from '@spartacus/core'
import { MbProductAction } from '../store/actions'
import { Actions } from '@ngrx/effects'

@Injectable({
  providedIn: 'root',
})
export class MbProductLoadingService extends ProductLoadingService {

  constructor (protected store: Store<StateWithProduct>,
              protected loadingScopes: LoadingScopesService,
              protected actions$: Actions,
              @Inject(PLATFORM_ID) protected platformId: any,
               protected eventService: EventService,
               protected cmsService: CmsService) {
    super(store, loadingScopes, actions$, platformId, eventService)
  }

  /**
   * Creates observable for providing specified product data for the scope
   *
   * @param productCode
   * @param scope
   */
  protected getProductForScope (productCode: string, scope: string) {
    const shouldLoad$ = this.store.pipe(
      select(
        ProductSelectors.getSelectedProductStateFactory(productCode, scope)
      ),
      map(
        (productState) =>
          !productState.loading && !productState.success && !productState.error
      ),
      distinctUntilChanged(),
      filter((x) => x)
    )

    const isLoading$ = this.store.pipe(
      select(
        ProductSelectors.getSelectedProductLoadingFactory(productCode, scope)
      )
    )

    const productLoadLogic$ = merge(
      shouldLoad$,
      ...this.getProductReloadTriggers(productCode, scope)
    ).pipe(
      debounceTime(0),
      withLatestFrom(this.cmsService.getCurrentPage()),
      take(1),
      filter(([, page]: [unknown, Page]) => page?.type === PageType.PRODUCT_PAGE),
      withLatestFrom(isLoading$),
      tap(([, isLoading]) => {
        if (!isLoading) {
          this.store.dispatch(
            new MbProductAction.MbLoadProduct(productCode, scope)
          )
        }
      })
    )

    const productData$ = this.store.pipe(
      select(ProductSelectors.getSelectedProductFactory(productCode, scope))
    )

    return using(
      () => productLoadLogic$.subscribe(),
      () => productData$
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }
}
